import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalProps,
} from '@heroui/react';
import React from 'react';

import { BaseButton } from '../BaseButton';

export interface BaseModalProps extends Omit<ModalProps, 'children'> {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  footerContent?: {
    cancelText?: string;
    confirmText?: string;
    onCancel?: () => void;
    onConfirm?: () => void;
    confirmColor?: 'primary' | 'danger';
    isLoading?: boolean;
    isDisabled?: boolean;
    /** Whether this is a destructive action (e.g. delete) */
    isDestructive?: boolean;
  };
  size?: 'sm' | 'md' | 'lg' | '2xl' | '3xl' | '4xl' | '5xl' | 'full';
}

export const BaseModal: React.FC<BaseModalProps> = ({
  title,
  subtitle,
  children,
  footer,
  footerContent,
  size = 'lg',
  classNames,
  ...modalProps
}) => {
  // Modals must stack above the SidePanel drawer (z-[2001] in SidePanel.tsx).
  const defaultClassNames = {
    header: 'p-8 border-b border-default-200',
    backdrop: 'bg-black/50 z-[2050]',
    body: 'p-6',
    footer: 'p-6 border-t border-default-200 gap-2',
    base: 'bg-content1 shadow-2xl border border-default-200',
    wrapper: 'z-[2100]',
  };

  return (
    <Modal
      size={size}
      classNames={{
        ...defaultClassNames,
        ...classNames,
      }}
      {...modalProps}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          {subtitle && (
            <p className="text-sm text-default-500 font-normal">{subtitle}</p>
          )}
        </ModalHeader>

        <ModalBody>{children}</ModalBody>

        <ModalFooter>
          {footer ||
            (footerContent && (
              <>
                {footerContent.onCancel && (
                  <BaseButton
                    onPress={footerContent.onCancel}
                    isDisabled={
                      footerContent.isLoading || footerContent.isDisabled
                    }
                  >
                    {footerContent.cancelText || 'Cancel'}
                  </BaseButton>
                )}
                {footerContent.onConfirm && (
                  <BaseButton
                    onPress={footerContent.onConfirm}
                    isLoading={footerContent.isLoading}
                    isDisabled={footerContent.isDisabled}
                    isConfirmButton
                    isDestructive={footerContent.isDestructive}
                  >
                    {footerContent.confirmText || 'Confirm'}
                  </BaseButton>
                )}
              </>
            ))}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
